import React from 'react';
import { Handle, Position } from 'reactflow';
import { GitBranch, GitFork } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

// ── Helper: parse cases config into normalised handle list ───────────────────
function parseCaseHandles(config) {
  const raw = config?.cases;
  if (!raw) return [];

  let cases = raw;

  // Accept JSON string
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try { cases = JSON.parse(trimmed); } catch { return []; }
    } else {
      // Comma-separated scalar list e.g. "admin, editor, viewer"
      cases = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }

  if (!Array.isArray(cases)) return [];

  return cases.map((item) => {
    if (typeof item === 'object' && item !== null) {
      return String(item.handle || item.case || item.label || '').trim().toLowerCase().replace(/\s+/g, '_');
    }
    return String(item).trim().toLowerCase().replace(/\s+/g, '_');
  }).filter(Boolean);
}

export default function ConditionNode({ data, selected }) {
  const { isDark } = useTheme();
  const isSwitch = data.type === 'SwitchCase';
  const Icon = isSwitch ? GitFork : GitBranch;

  // ── For SwitchCase: derive dynamic handles from config ───────────────────
  const rawHandles = isSwitch ? parseCaseHandles(data.config) : [];

  // Filter out any custom handle that is named 'default' (case-insensitive) to prevent double default handles
  const nonDefaultHandles = rawHandles.filter(h => h.toLowerCase() !== 'default');

  const caseHandles = nonDefaultHandles.length > 0
    ? nonDefaultHandles
    : ['case_a', 'case_b'];

  // Always append exactly one "default" handle at the end
  const allSwitchHandles = [...caseHandles, 'default'];

  // Space handles evenly along the right edge
  const totalHandles = allSwitchHandles.length;
  const handlePositions = allSwitchHandles.map((_, i) =>
    ((i + 1) / (totalHandles + 1)) * 100
  );

  // Height scales with number of switch handles; minimum 90px
  const nodeHeight = isSwitch ? Math.max(90, totalHandles * 28 + 30) : 'auto';

  return (
    <div
      style={{
        background: isDark ? '#0d1117' : '#ffffff',
        border: `2px solid ${selected ? '#F87217' : '#EF4444'}`,
        borderRadius: 12,
        // Add right padding for SwitchCase to create a dedicated column for handle labels (prevents overlapping)
        padding: isSwitch ? '10px 68px 10px 14px' : '10px 14px',
        minWidth: isSwitch ? 190 : 170,
        minHeight: nodeHeight,
        boxShadow: selected
          ? '0 0 0 3px rgba(248,114,23,0.25), 0 4px 16px rgba(0,0,0,0.1)'
          : '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.18s ease',
        cursor: 'default',
        position: 'relative',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: 10,
          height: 10,
          background: '#EF4444',
          border: `2px solid ${isDark ? '#0d1117' : '#ffffff'}`,
          left: -6,
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: 'rgba(239,68,68,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={14} color="#EF4444" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: '#EF4444',
              marginBottom: 1,
            }}
          >
            Logic
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#e6edf3' : '#111827' }}>
            {data.label || data.type}
          </div>
        </div>
      </div>

      {/* ── IfCondition: static TRUE / FALSE handles ── */}
      {!isSwitch && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            style={{
              width: 10, height: 10,
              background: '#10B981',
              border: `2px solid ${isDark ? '#0d1117' : '#ffffff'}`,
              right: -6, top: '35%',
            }}
          />
          <span style={{
            position: 'absolute', right: 10, top: '28%',
            fontSize: 9, color: '#10B981', fontWeight: 700, pointerEvents: 'none',
          }}>
            TRUE
          </span>

          <Handle
            type="source"
            position={Position.Right}
            id="false"
            style={{
              width: 10, height: 10,
              background: '#EF4444',
              border: `2px solid ${isDark ? '#0d1117' : '#ffffff'}`,
              right: -6, top: '65%',
            }}
          />
          <span style={{
            position: 'absolute', right: 10, top: '60%',
            fontSize: 9, color: '#EF4444', fontWeight: 700, pointerEvents: 'none',
          }}>
            FALSE
          </span>
        </>
      )}

      {/* ── SwitchCase: dynamic handles, one per case + default ── */}
      {isSwitch && allSwitchHandles.map((handleId, i) => {
        const isDefault = handleId === 'default';
        const color = isDefault ? '#94A3B8' : '#A855F7';
        const topPct = handlePositions[i];

        return (
          <React.Fragment key={handleId}>
            <Handle
              type="source"
              position={Position.Right}
              id={handleId}
              style={{
                width: 10,
                height: 10,
                background: color,
                border: `2px solid ${isDark ? '#0d1117' : '#ffffff'}`,
                right: -6,
                top: `${topPct}%`,
              }}
            />
            {/* Sits beautifully inside the right padding zone without overlapping the text on the left */}
            <span style={{
              position: 'absolute',
              right: 12,
              top: `calc(${topPct}% - 6px)`,
              fontSize: 8,
              color: color,
              fontWeight: 800,
              pointerEvents: 'none',
              maxWidth: 55,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'right',
              fontFamily: 'monospace',
            }}>
              {handleId}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}